import { Stack, Typography, useTheme } from '@mui/material';
import { useForm } from 'react-hook-form';

import { FormProvider, GlassEffectDialog, RHFTextField, Scrollbar } from '@app/components';
import { IAddTaskBoardSchema, IMember, ISideBarSchema } from '../types';
import { selectBoardDetailsState } from '../selector';
import { useRequestState } from '@app/hooks';
import { CustomAvatar } from '@app/components/custom-avatar';
import { Strings } from '@app/constants';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  iconName: string;
  data?: ISideBarSchema;
}

const BoardDetailDialog = ({ open, onClose, iconName, data }: Props) => {
  const methods = useForm<IAddTaskBoardSchema>();
  const theme = useTheme();

  const { data: boardDetails } = useRequestState({
    stateSelector: selectBoardDetailsState,
    errorShown: true,
  });

  return (
    <>
      <FormProvider methods={methods}>
        <GlassEffectDialog
          open={open}
          onClose={onClose}
          title={'Board Details'}
          backgroundImage={boardDetails?.image}
          boardIcon={iconName}
          height="100vh"
        >
          <Scrollbar sx={{ height: '60vh' }}>
            <Stack sx={{ width: '380px', px: 2 }} gap={1.5}>
              <Typography sx={{ color: theme.palette.taskboard.darkSlateGray }}>
                {Strings.taskBoard.boardAdmins}
              </Typography>
              <Stack sx={{ display: 'flex', flexDirection: 'row' }}>
                <CustomAvatar
                  sx={{
                    backgroundColor: theme.palette.taskboard.darkSlateGray,
                    color: theme.palette.common.white,
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    border: `2px ${theme.palette.taskboard.darkSlateGray} `,
                  }}
                  name={`${data?.user?.firstName ?? ''} ${data?.user?.lastName ?? ''}`}
                  color="info"
                  src={data?.user?.profileImage}
                />
                <Stack ml={1.5} spacing={-0.5}>
                  <Typography sx={{ fontWeight: 600 }}>{`${data?.user?.firstName ?? ''} ${
                    data?.user?.lastName ?? ''
                  }`}</Typography>
                  <Typography sx={{ fontSize: '10px', fontWeight: 500 }}>
                    {Strings.taskBoard.workspaceOwner}
                  </Typography>
                </Stack>
              </Stack>
              <Typography sx={{ color: theme.palette.taskboard.darkSlateGray }}>
                {Strings.taskBoard.boardMembers}
              </Typography>
              {boardDetails?.members?.map((board: IMember, index: number) => {
                return (
                  <Stack key={index} sx={{ display: 'flex', flexDirection: 'row', gap: '9px' }}>
                    <CustomAvatar
                      sx={{
                        backgroundColor: theme.palette.taskboard.darkSlateGray,
                        color: theme.palette.common.white,
                        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                        border: `2px ${theme.palette.taskboard.darkSlateGray} `,
                      }}
                      name={board?.firstName ?? 'NA'}
                      color="info"
                      src={board?.profileImage}
                    />
                    <Stack mt={1} fontWeight={600}>
                      {board?.firstName}
                    </Stack>
                  </Stack>
                );
              })}

              <Stack gap={1}>
                <Typography sx={{ fontWeight: 550, color: theme.palette.taskboard.darkSlateGray }}>
                  {Strings.taskBoard.description}
                </Typography>
                <Stack
                  sx={{
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    border: `.1px solid ${theme.palette.taskboard.raisinBlack}`,
                    borderRadius: '10px',
                  }}
                >
                  <RHFTextField
                    defaultValue={data?.description ?? ''}
                    label=""
                    name="description"
                    multiline
                    minRows={3}
                    disabled
                    sx={{
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: theme.palette.taskboard.raisinBlack,
                      },
                    }}
                  />
                </Stack>
              </Stack>
            </Stack>
          </Scrollbar>
        </GlassEffectDialog>
      </FormProvider>
    </>
  );
};

export { BoardDetailDialog };
